import {inject, injectable} from 'inversify'
import {action, computed, makeObservable, observable} from 'mobx'

import {IChangeBankAccountDetails} from '@/pages/ChangeBankAccountDetails'
import {POSOutletDetailsStoreSymbol} from '@/pages/POSOutletDetails/POSOutletDetails'
import {OutletDetailsStore} from '@/stores/outlets/OutletDetailsStore'
import {OnlineOutletDetailsStoreSymbol} from '@/pages/OnlineOutletDetails/OnlineOutletDetails'
import {OutletDetailsResponseModel} from '@/stores/outlets'
import {BankAccountsStore, DossierBankAccount, DossierBankDetail} from '@/stores/bank-accounts'
import {BankAccountsStoreSymbol} from '@/pages/BankAccounts/BankAccounts'
import {
    AccountType,
    BankDetailReason,
    ChangeAllOutletModel,
    ChangeBankAccountDetailsRequest,
    ModalState
} from '@/stores/change-bank-account-details/models'
import {
    changeBankAccountDetails,
    getBankDetailReasons
} from '@/stores/change-bank-account-details/services/fetchers'
import {ProfileStoreSymbol} from '@/pages/Profile'
import {ProfileStore} from '@/stores/profile/ProfileStore'
import {
    DossierMerchantInfoStore,
    DossierMerchantInfoStoreSymbol
} from '@/stores/profile/DossierMerchantInfo'
import {captureSentryError, getErrorMessage} from '@/services/sentry-helper'
import {LoadingState} from '@/types'
import {getRouterStore} from '@/router/utils'
import {PATHS} from './constants/constants'

@injectable()
export class ChangeBankAccountDetailsStore implements IChangeBankAccountDetails {
    private outletStore: OutletDetailsStore
    private bankAccountsStore: BankAccountsStore
    private profileStore: ProfileStore
    private dossierMerchantInfoStore: DossierMerchantInfoStore
    selectedBankAccount: DossierBankAccount = null
    loadingState: LoadingState = LoadingState.IDLE
    modalState: ModalState = {
        confirm: false,
        success: false,
        error: false
    }
    currentData: Pick<ChangeBankAccountDetailsRequest, 'paymentMethod' | 'changeBankDetailReason'> =
        null
    selectAccountType = null
    bankDetailReasons: BankDetailReason[] = []
    reasonsLoadingState: LoadingState = LoadingState.IDLE

    constructor(
        @inject(POSOutletDetailsStoreSymbol) private posOutletStore: OutletDetailsStore,
        @inject(OnlineOutletDetailsStoreSymbol) private onlineOutletStore: OutletDetailsStore,
        @inject(BankAccountsStoreSymbol) private _bankAccountsStore: BankAccountsStore,
        @inject(ProfileStoreSymbol) profileStore: ProfileStore,
        @inject(DossierMerchantInfoStoreSymbol) dossierMerchantInfoStore: DossierMerchantInfoStore
    ) {
        this.outletStore = this.detectOutletStore()
        this.bankAccountsStore = _bankAccountsStore
        this.profileStore = profileStore
        this.dossierMerchantInfoStore = dossierMerchantInfoStore

        makeObservable(this, {
            selectedBankAccount: observable,
            loadingState: observable,
            modalState: observable,
            selectAccountType: observable,
            bankDetailReasons: observable,
            reasonsLoadingState: observable,

            currentFullBankAccountRelatedStores: computed,
            outletDetails: computed,
            isLoading: computed,
            accountType: computed,
            bankAccounts: computed,
            bankAccountsLoading: computed,
            hasSettlementBlocks: computed,
            hasDirectDebitBlocks: computed,
            hasAllBlocks: computed,
            associatedOutlets: computed,
            currentBankAccount: computed,

            changeBankDetails: action,
            openConfirmBankDetailsModal: action,
            handleModalState: action,
            onBack: action,
            setSelectAccountType: action,
            loadBankDetailReasons: action
        })
    }

    get isLoading(): boolean {
        return this.loadingState === LoadingState.LOADING
    }

    get outletDetails(): OutletDetailsResponseModel {
        return this.outletStore.details
    }

    get currentBankAccount(): DossierBankDetail {
        if (this.accountType === 'settlement') {
            return this.outletDetails?.settlementAccounts[0]
        } else {
            return this.outletDetails?.directDebitAccounts[0]
        }
    }

    get currentFullBankAccountDetails(): DossierBankAccount {
        if (this.accountType === 'settlement') {
            return this.bankAccountsStore._bankAccounts?.find(
                (account) => account.bankDetail.id === this.outletDetails?.settlementAccounts[0].id
            )
        } else {
            return this.bankAccountsStore._bankAccounts?.find(
                (account) => account.bankDetail.id === this.outletDetails?.directDebitAccounts[0].id
            )
        }
    }

    get bankAccounts(): DossierBankAccount[] {
        return this.bankAccountsStore?.bankAccounts?.filter(
            (account) => account.bankDetail.id !== this.currentBankAccount?.id
        )
    }

    get bankAccountsLoading(): boolean {
        return this.bankAccountsStore.loadingState === LoadingState.LOADING
    }

    get accountType(): AccountType {
        const path = window.location.pathname

        if (path.includes(PATHS.DIRECT_DEBIT)) return 'directDebit'

        if (path.includes(PATHS.SETTLEMENTS)) return 'settlement'

        return 'directDebit'
    }

    get hasSettlementBlocks(): boolean {
        return this.outletStore.hasSettlementBlocks
    }
    get hasDirectDebitBlocks(): boolean {
        return this.outletStore.hasDirectDebitBlocks
    }
    get hasAllBlocks(): boolean {
        return this.outletStore.hasAllBlocks
    }

    get shouldChangeAllOutletsBankDetails(): ChangeAllOutletModel {
        const mainInfo = this.dossierMerchantInfoStore.mainInfo
        const accountType = this.selectAccountType

        if (!mainInfo || !accountType) {
            return {
                directDebit: false,
                settlement: false
            }
        }

        switch (accountType) {
            case 'directDebit':
                return {
                    directDebit:
                        mainInfo.settlementType === 'DD' && mainInfo.chargeMerchant === 'UBER_ID',
                    settlement: false
                }

            case 'settlement':
                return {
                    directDebit: false,
                    settlement: mainInfo.settlementPayment === 'uberid'
                }

            case 'all':
                const isDD = mainInfo.settlementType === 'DD'
                const isUberId = mainInfo.chargeMerchant === 'UBER_ID'
                const isUberPayment = mainInfo.settlementPayment === 'uberid'

                if (isDD && isUberId && !isUberPayment) {
                    return {
                        directDebit: true,
                        settlement: false
                    }
                }

                if (isUberPayment && !isDD) {
                    return {
                        directDebit: false,
                        settlement: true
                    }
                }

                if (isDD && isUberId && isUberPayment) {
                    return {
                        directDebit: true,
                        settlement: true
                    }
                }

                return {
                    directDebit: false,
                    settlement: false
                }

            default:
                return {
                    directDebit: false,
                    settlement: false
                }
        }
    }

    get currentFullBankAccountRelatedStores(): string[] {
        return this.currentFullBankAccountDetails?.stores?.map((store) => store.storeName) ?? []
    }

    get associatedOutlets(): string[] {
        const changeAllModel = this.shouldChangeAllOutletsBankDetails

        if (changeAllModel.directDebit || changeAllModel.settlement) {
            return this.currentFullBankAccountRelatedStores
        }

        return [this.outletDetails?.storeName].filter(Boolean)
    }

    openConfirmBankDetailsModal = (
        data: Pick<ChangeBankAccountDetailsRequest, 'paymentMethod' | 'changeBankDetailReason'>
    ) => {
        this.currentData = data

        this.handleModalState('confirm', true)
    }

    changeBankDetails = async () => {
        try {
            this.loadingState = LoadingState.LOADING
            const prepareData: ChangeBankAccountDetailsRequest = {
                bankAccountNumber: this.selectedBankAccount?.bankDetail?.bankAccountNumber,
                bankAccountType: this.selectedBankAccount?.bankDetail?.bankAccountTypeCode,
                dossierId: this.dossierMerchantInfoStore?.mainInfo?.dsrId?.toString(),
                initiator: this.profileStore.profileData?.email,
                outletIds: [this.outletDetails?.id?.toString()],
                sortCode: this.selectedBankAccount?.bankDetail?.sortCode,
                firstName: this.profileStore?.profileData?.firstName,
                surname: this.profileStore?.profileData?.lastName,
                paymentMethod: this.currentData.paymentMethod,
                changeBankDetailReason: this.currentData.changeBankDetailReason
            }

            const response = await changeBankAccountDetails(prepareData)

            if (response.status === 200) {
                this.loadingState = LoadingState.DONE
                this.handleModalState('success', true)
                this.onCloseConfirmModal()
                this.initStore()
            } else {
                this.loadingState = LoadingState.FAILED
                this.handleModalState('error', true)
                this.onCloseConfirmModal()
                throw new Error(`HTTP error! Status: ${response.status}`)
            }
        } catch (error) {
            captureSentryError({
                name: 'ChangeBankAccountDetailsStore changeBankDetails Error',
                description: getErrorMessage(error, 'Failed to change bank details'),
                fingerprint: ['change-bank-account-details-store-change-bank-details'],
                error
            })
            this.loadingState = LoadingState.FAILED
            console.error(error)
        }
    }

    private detectOutletStore(): OutletDetailsStore {
        const path = window.location.pathname

        if (path.includes('/pos-outlets/')) {
            return this.posOutletStore
        }

        if (path.includes('/online-outlets/')) {
            return this.onlineOutletStore
        }

        throw new Error('Can not detect Outlet Store')
    }

    selectBankAccount = (account: DossierBankAccount) => {
        this.selectedBankAccount = account
    }

    onBack = () => {
        this.setSelectAccountType(null)
        getRouterStore().back()
    }

    onCloseConfirmModal = () => {
        this.handleModalState('confirm', false)
    }

    onCloseSuccessModal = async () => {
        this.handleModalState('success', false)
        this.setSelectAccountType(null)
        await this.loadData()
        this.onBack()
    }

    onCloseErrorModal = async () => {
        this.handleModalState('error', false)
        this.setSelectAccountType(null)
        await this.loadData()
        this.onBack()
    }

    initStore = () => {
        this.handleModalState('confirm', false)
        this.currentData = null
    }

    loadData = async () => {
        await this.bankAccountsStore.loadDossierBankAccounts()
    }

    handleModalState = (type: keyof ModalState, isOpen: boolean): void => {
        this.modalState[type] = isOpen
    }

    setSelectAccountType = (type: AccountType): void => {
        this.selectAccountType = type
    }

    loadBankDetailReasons = async (): Promise<void> => {
        try {
            this.reasonsLoadingState = LoadingState.LOADING
            const response = await getBankDetailReasons()
            this.bankDetailReasons = response.result
            this.reasonsLoadingState = LoadingState.DONE
        } catch (error) {
            captureSentryError({
                name: 'ChangeBankAccountDetailsStore loadBankDetailReasons Error',
                description: getErrorMessage(error, 'Failed to load bank detail reasons'),
                fingerprint: ['change-bank-account-details-store-load-reasons'],
                error
            })
            this.reasonsLoadingState = LoadingState.FAILED
            console.error('Failed to load bank detail reasons:', error)
        }
    }
}
